import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useAuth } from '../../auth/AuthProvider';
import {
  AdminChatRealtimeEvent,
  applyAdminChatRealtimeEvent,
  buildAdminChatWebSocketUrl,
} from './chatRealtime';

const RECONNECT_DELAY_MS = 3000;
const TYPING_RECEIVE_TIMEOUT_MS = 5000;

export type AdminChatRealtimeController = {
  readonly isPatientTyping: (patientId: string) => boolean;
  readonly sendTyping: (patientId: string, isTyping: boolean) => void;
};

export function useAdminChatRealtime(
  enabled: boolean,
  patientId: string,
): AdminChatRealtimeController {
  const { tokens } = useAuth();
  const queryClient = useQueryClient();
  const reconnectTimerRef = useRef<number | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const typingTimersRef = useRef<Map<string, number>>(new Map());
  const [typingPatients, setTypingPatients] = useState<ReadonlySet<string>>(() => new Set());

  const clearTypingTimer = useCallback((targetPatientId: string) => {
    const timer = typingTimersRef.current.get(targetPatientId);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      typingTimersRef.current.delete(targetPatientId);
    }
  }, []);

  const setPatientTyping = useCallback(
    (targetPatientId: string, nextIsTyping: boolean) => {
      setTypingPatients((current) => {
        const isCurrentlyTyping = current.has(targetPatientId);
        if (nextIsTyping === isCurrentlyTyping) {
          return current;
        }
        const next = new Set(current);
        if (nextIsTyping) {
          next.add(targetPatientId);
        } else {
          next.delete(targetPatientId);
        }
        return next;
      });
    },
    [],
  );

  useEffect(() => {
    if (!enabled || !tokens?.access_token) {
      return;
    }

    let active = true;
    const timers = typingTimersRef.current;

    const clearReconnectTimer = () => {
      if (reconnectTimerRef.current !== null) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    const connect = () => {
      clearReconnectTimer();
      const socket = new WebSocket(buildAdminChatWebSocketUrl(tokens.access_token, patientId));
      socketRef.current = socket;
      socket.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data) as AdminChatRealtimeEvent;
          if (parsed.type === 'chat.typing') {
            const typing = parsed.payload.typing;
            if (!typing || typing.actor_role !== 'patient') {
              return;
            }
            const targetPatientId = parsed.patient_id;
            const isTyping = typing.is_typing === true;
            clearTypingTimer(targetPatientId);
            if (isTyping) {
              setPatientTyping(targetPatientId, true);
              const timer = window.setTimeout(() => {
                typingTimersRef.current.delete(targetPatientId);
                setPatientTyping(targetPatientId, false);
              }, TYPING_RECEIVE_TIMEOUT_MS);
              typingTimersRef.current.set(targetPatientId, timer);
            } else {
              setPatientTyping(targetPatientId, false);
            }
            return;
          }
          applyAdminChatRealtimeEvent(queryClient, parsed);
        } catch {
          // Ignore malformed events and keep the active socket alive.
        }
      };
      socket.onclose = () => {
        if (socketRef.current === socket) {
          socketRef.current = null;
        }
        if (!active) {
          return;
        }
        reconnectTimerRef.current = window.setTimeout(connect, RECONNECT_DELAY_MS);
      };
      socket.onerror = () => {
        socket.close();
      };
    };

    connect();

    return () => {
      active = false;
      clearReconnectTimer();
      const socket = socketRef.current;
      socketRef.current = null;
      socket?.close();
      timers.forEach((timer) => window.clearTimeout(timer));
      timers.clear();
      setTypingPatients(new Set());
    };
  }, [
    clearTypingTimer,
    enabled,
    patientId,
    queryClient,
    setPatientTyping,
    tokens?.access_token,
  ]);

  const isPatientTyping = useCallback(
    (targetPatientId: string): boolean => typingPatients.has(targetPatientId),
    [typingPatients],
  );

  const sendTyping = useCallback((targetPatientId: string, isTyping: boolean): void => {
    if (!targetPatientId) {
      return;
    }
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      return;
    }
    try {
      socket.send(
        JSON.stringify({
          type: 'typing',
          patient_id: targetPatientId,
          is_typing: isTyping,
        }),
      );
    } catch {
      // Swallow send errors; the next reconnect / retry will reconcile state.
    }
  }, []);

  return { isPatientTyping, sendTyping };
}
